import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextTitle } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _, { debounce, drop } from 'lodash';
import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { NewAccountStackParamList, NewAccountStackRoutes } from './type';
import { Dropdown } from '@/design-system/components/dropdown';
import { CurrenciesContext } from '@/currencies/currency.context';
import { keyGenerator } from '@/utils/keyGenerator';

export interface NewAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const NewAccountWebZeroScreen: React.FC<NewAccountProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn } = sxStyles;

  const { currencies } = useContext(CurrenciesContext);
  const [filter, setFilter] = useState<string>('');
  const accountAssetsFilter = useMemo(() => {
    return currencies
      ?.map((curr) => {
        return {
          label: curr.display_symbol,
          value: curr.display_symbol
        };
      })
      .filter((item) => {
        if (filter && filter.length > 0) {
          return item.value.toLowerCase().includes(filter.toLowerCase());
        }
        return true;
      });
  }, [currencies, filter]);

  const navigation = useNavigation<NativeStackNavigationProp<NewAccountStackParamList>>();

  const [account, setAccount] = useState<any>({
    name: undefined,
    assets: undefined
  });
  const [errorMsg, setErrorMsg] = useState<any>({ assets: undefined, name: undefined });

  const [currentKey, setKey] = useState(keyGenerator('New-Account'));
  const onChangeView = () => {
    setKey(keyGenerator('New-Account'));
  };

  const changeNewAccount = () => {
    let validate = true;
    let errorMsgFlag = { assets: undefined, name: undefined };
    if (!account.name) {
      setErrorMsg({ ...errorMsgFlag, name: t('card-account-management::credit-card-new-account.glossary.field-required') });
      validate = false;
    } else if (!account.assets) {
      setErrorMsg({ ...errorMsgFlag, assets: t('card-account-management::credit-card-new-account.glossary.field-required') });
      validate = false;
    } else setErrorMsg(errorMsgFlag);

    if (validate) {
      onData({ data: account });
      navigation.navigate(NewAccountStackRoutes.Confirm, { onLayout, data: account, func, onReturnData, refresh, onChangeView });
    }
  };

  const searchValue = useCallback(
    debounce((value) => setFilter(value), 300),
    []
  );

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={onLayout}
      key={currentKey}>
      <ModalHeader title={t('card-account-management::credit-card-new-account.glossary.new-account')} closeModal={onClose} />
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account-management::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          <UniInput
            value={account.name}
            labelText={t('card-account-management::credit-card-new-account.glossary.account-name')}
            errorText={errorMsg.name}
            onValueChange={(value) => {
              setAccount({
                ...account,
                name: value?.toUpperCase()
              });
            }}
          />
          <View>
            <Dropdown
              value={account.assets}
              label={t('card-account-management::credit-card-new-account.glossary.assets')}
              multiple
              options={accountAssetsFilter || []}
              sx={{ width: '100%' }}
              dropdownSx={{ maxHeight: 100 }}
              onSearch={searchValue}
              onValueChange={(value) => {
                setAccount({
                  ...account,
                  assets: value
                });
              }}
            />
          </View>
        </View>
        <View sx={btnView}>
          <UniButton
            sx={btn}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::credit-card-new-account.glossary.button--update')}
            onPress={changeNewAccount}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  }
};
